import { useRef, useState, type ReactNode } from "react";

/**
 * Hover/focus popover in the POC `.tooltip-card` style. The card is rendered
 * `position: fixed` (measured from the trigger) so it escapes scroll
 * containers like `.table-wrap`, which would clip an absolutely-positioned
 * card. Keyboard-accessible: the trigger is focusable and shows on focus.
 */
export function HoverDetail({ summary, children }: { summary: ReactNode; children: ReactNode }) {
  const anchor = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; above: boolean } | null>(null);

  const show = () => {
    const rect = anchor.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.min(360, window.innerWidth - 48);
    const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 24));
    // Flip above the trigger when the card would run off the bottom.
    const above = rect.bottom + 180 > window.innerHeight;
    setPos({ left, top: above ? rect.top - 8 : rect.bottom + 8, above });
  };
  const hide = () => setPos(null);

  return (
    <span
      ref={anchor}
      className="inline-info"
      tabIndex={0}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {summary}
      {pos && (
        <span
          className="tooltip-card"
          role="tooltip"
          style={{
            display: "block",
            position: "fixed",
            left: pos.left,
            top: pos.top,
            transform: pos.above ? "translateY(-100%)" : undefined,
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
          }}
        >
          {children}
        </span>
      )}
    </span>
  );
}
